import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Text,
  SimpleGrid,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Badge,
  FormControl,
  FormLabel,
  Textarea,
  Switch,
  FormHelperText,
  Image
} from '@chakra-ui/react';
import { FiEdit, FiTrash2, FiPlus, FiUpload } from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// For static files (uploads), we need the base URL without /api prefix
// Always use full backend URL for images (proxy doesn't work for img src)
// Extract backend URL from api instance or environment, handling proxy paths

const getBackendBaseUrl = () => {
  // Try REACT_APP_BACKEND_URL first (explicit backend URL for static files)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Check REACT_APP_API_URL - if it's a full URL, use it; if it's a proxy path, extract from api instance
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl && apiUrl.startsWith('http')) {
    return apiUrl;
  }
  
  // If apiUrl is a proxy path (like '/api'), get the actual backend URL from axios instance
  const apiBaseURL = api.defaults.baseURL;
  if (apiBaseURL && apiBaseURL.startsWith('http')) {
    return apiBaseURL;
  }
  
  // If still no valid URL and we're in browser, use relative URL (same origin)
  // This works when frontend and backend are served from same domain
  if (typeof window !== 'undefined') {
    return ''; // Empty string means relative URL
  }
  
  // Last resort: return empty string to use relative URLs
  return '';
};

const BACKEND_BASE_URL = getBackendBaseUrl();

const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http')) return logoUrl;
  
  // Ensure logoUrl starts with / and remove any /api prefix
  let cleanUrl = logoUrl.trim();
  if (cleanUrl.startsWith('/api')) {
    cleanUrl = cleanUrl.replace(/^\/api/, '');
  }
  if (!cleanUrl.startsWith('/')) {
    cleanUrl = '/' + cleanUrl;
  }
  
  // Always use full backend URL for images
  const fullUrl = `${BACKEND_BASE_URL}${cleanUrl}`;
  
  // Debug log
  console.log('Logo URL conversion:', { 
    original: logoUrl, 
    cleanUrl, 
    BACKEND_BASE_URL, 
    fullUrl
  });
  
  return fullUrl;
};

const SettingsEntities = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    postcode: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    vat_number: '',
    logo_url: '',
    is_active: true
  });

  useEffect(() => {
    loadEntities();
  }, []);

  const loadEntities = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/entities');
      setEntities(response.data.data?.entities || []);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('messages.server_error'),
        status: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (entity = null) => {
    if (entity) {
      setEditingEntity(entity);
      setFormData({
        name: entity.name || '',
        address: entity.address || '',
        postcode: entity.postcode || '',
        city: entity.city || '',
        country: entity.country || '',
        phone: entity.phone || '',
        email: entity.email || '',
        website: entity.website || '',
        vat_number: entity.vat_number || '',
        logo_url: entity.logo_url || '',
        is_active: entity.is_active === 1 || entity.is_active === true
      });
    } else {
      setEditingEntity(null);
      setFormData({
        name: '',
        address: '',
        postcode: '',
        city: '',
        country: '',
        phone: '',
        email: '',
        website: '',
        vat_number: '',
        logo_url: '',
        is_active: true
      });
    }
    onOpen();
  };

  const handleSave = async () => {
    try {
      if (!formData.name) {
        toast({
          title: t('common.error'),
          description: t('entity.name_required', { defaultValue: 'Name is required' }),
          status: 'error'
        });
        return;
      }

      if (editingEntity) {
        await api.put(`/api/entities/${editingEntity.id}`, formData);
        toast({
          title: t('common.success'),
          description: t('entity.updated', { defaultValue: 'Entity updated' }),
          status: 'success'
        });
      } else {
        await api.post('/api/entities', formData);
        toast({
          title: t('common.success'),
          description: t('entity.created', { defaultValue: 'Entity created' }),
          status: 'success'
        });
      }
      onClose();
      loadEntities();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.error || t('messages.server_error'),
        status: 'error'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('entity.confirm_delete', { defaultValue: 'Are you sure you want to delete this entity?' }))) {
      return;
    }
    try {
      await api.delete(`/api/entities/${id}`);
      toast({
        title: t('common.success'),
        description: t('entity.deleted', { defaultValue: 'Entity deleted' }),
        status: 'success'
      });
      loadEntities();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.error || t('messages.server_error'),
        status: 'error'
      });
    }
  };

  const handleLogoUpload = async (entityId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/api/entities/${entityId}/logo`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.success && response.data.data?.url) {
        toast({
          title: t('common.success'),
          description: t('entity.logo_uploaded', { defaultValue: 'Logo uploaded' }),
          status: 'success'
        });
        loadEntities();
        return response.data.data.url;
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast({
        title: t('common.error'),
        description: error.response?.data?.error || error.response?.data?.details || t('messages.server_error'),
        status: 'error'
      });
      return null;
    }
  };

  const isAdmin = user?.role === 'admin';

  if (!isAdmin) {
    return (
      <Box>
        <Heading size="lg" mb={6}>{t('settings.entities', { defaultValue: 'Entities' })}</Heading>
        <Text>{t('common.access_denied', { defaultValue: 'Access denied' })}</Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">{t('settings.entities', { defaultValue: 'Entities' })}</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={() => handleOpenModal()}>
          {t('entity.add', { defaultValue: 'Add Entity' })}
        </Button>
      </HStack>

      <Card>
        <CardBody>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>{t('settings.pdf_logo_title', { defaultValue: 'Logo' })}</Th>
                <Th>{t('entity.name', { defaultValue: 'Name' })}</Th>
                <Th>{t('entity.email', { defaultValue: 'Email' })}</Th>
                <Th>{t('entity.phone', { defaultValue: 'Phone' })}</Th>
                <Th>{t('entity.status', { defaultValue: 'Status' })}</Th>
                <Th>{t('common.actions', { defaultValue: 'Actions' })}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {entities.map((entity) => (
                <Tr key={entity.id}>
                  <Td>
                    {entity.logo_url ? (
                      <Image
                        src={getLogoUrl(entity.logo_url)}
                        alt={entity.name}
                        boxSize="40px"
                        objectFit="contain"
                        borderRadius="md"
                        bg="gray.50"
                        p={1}
                        onError={(e) => {
                          console.error('Logo load error:', entity.logo_url, 'Full URL:', getLogoUrl(entity.logo_url), e);
                        }}
                        fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='40' height='40' fill='%23e2e8f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='12' fill='%2394a3b8'%3ENo Logo%3C/text%3E%3C/svg%3E"
                      />
                    ) : (
                      <Box
                        boxSize="40px"
                        bg="gray.100"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text fontSize="xs" color="gray.400">-</Text>
                      </Box>
                    )}
                  </Td>
                  <Td fontWeight="medium">{entity.name}</Td>
                  <Td>{entity.email || '-'}</Td>
                  <Td>{entity.phone || '-'}</Td>
                  <Td>
                    <Badge colorScheme={entity.is_active === 1 ? 'green' : 'red'}>
                      {entity.is_active === 1 ? t('common.active', { defaultValue: 'Active' }) : t('common.inactive', { defaultValue: 'Inactive' })}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        icon={<FiEdit />}
                        size="sm"
                        onClick={() => handleOpenModal(entity)}
                        aria-label={t('common.edit')}
                      />
                      <IconButton
                        icon={<FiTrash2 />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDelete(entity.id)}
                        aria-label={t('common.delete')}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingEntity ? t('entity.edit', { defaultValue: 'Edit Entity' }) : t('entity.add', { defaultValue: 'Add Entity' })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                <FormControl isRequired>
                  <FormLabel>{t('entity.name', { defaultValue: 'Name' })}</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('entity.email', { defaultValue: 'Email' })}</FormLabel>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('entity.phone', { defaultValue: 'Phone' })}</FormLabel>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('entity.website', { defaultValue: 'Website' })}</FormLabel>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('settings.address', { defaultValue: 'Address' })}</FormLabel>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('settings.postcode', { defaultValue: 'Postcode' })}</FormLabel>
                  <Input
                    value={formData.postcode}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('settings.city', { defaultValue: 'City' })}</FormLabel>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('settings.country', { defaultValue: 'Country' })}</FormLabel>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('settings.vat_number', { defaultValue: 'VAT Number' })}</FormLabel>
                  <Input
                    value={formData.vat_number}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                  />
                </FormControl>
                {editingEntity && (
                  <FormControl>
                    <FormLabel>{t('settings.pdf_logo_title', { defaultValue: 'Logo' })}</FormLabel>
                    {formData.logo_url && (
                      <Box mb={3}>
                        <Image
                          src={getLogoUrl(formData.logo_url)}
                          alt="Current logo"
                          boxSize="80px"
                          objectFit="contain"
                          borderRadius="md"
                          bg="gray.50"
                          p={2}
                          border="1px solid"
                          borderColor="gray.200"
                          onError={(e) => {
                            console.error('Logo load error in modal:', formData.logo_url, 'Full URL:', getLogoUrl(formData.logo_url), e);
                          }}
                          fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23e2e8f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='12' fill='%2394a3b8'%3ENo Logo%3C/text%3E%3C/svg%3E"
                        />
                      </Box>
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const url = await handleLogoUpload(editingEntity.id, file);
                          if (url) {
                            setFormData({ ...formData, logo_url: url });
                          }
                        }
                      }}
                    />
                    {formData.logo_url && (
                      <FormHelperText>{t('entity.current_logo', { defaultValue: 'Current logo' })}: {formData.logo_url}</FormHelperText>
                    )}
                  </FormControl>
                )}
                <FormControl>
                  <FormLabel>{t('common.status', { defaultValue: 'Status' })}</FormLabel>
                  <Switch
                    isChecked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <FormHelperText>
                    {formData.is_active ? t('common.active', { defaultValue: 'Active' }) : t('common.inactive', { defaultValue: 'Inactive' })}
                  </FormHelperText>
                </FormControl>
              </SimpleGrid>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button colorScheme="brand" onClick={handleSave}>
              {t('common.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SettingsEntities;


